import { useEffect, useRef, useState } from "react";
import api from "../lib/api";
import { Input } from "./ui/input";
import { ChevronsUpDown, Plus } from "lucide-react";
import NuovaCompagniaDialog from "./NuovaCompagniaDialog";

// Combobox autocomplete predittivo per la Compagnia Assicurativa.
// Attinge dalla tabella anagrafiche "compagnie"; se il nome digitato non esiste, propone
// "Aggiungi [nome] come nuova compagnia" che apre la modale di creazione precompilata.
export default function CompagniaField({ value, onChange, onCommit, testId, className = "", placeholder = "Digita o seleziona compagnia…" }) {
  const [val, setVal] = useState(value || "");
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [typed, setTyped] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [createName, setCreateName] = useState("");
  const ref = useRef(null);
  const valRef = useRef(value || "");

  useEffect(() => {
    const nv = value || "";
    if (nv !== valRef.current) { valRef.current = nv; setVal(nv); setTyped(false); }
  }, [value]);

  const loadItems = () => {
    api.get("/anagrafiche/compagnie")
      .then(({ data }) => setItems((data || []).map((r) => r.denominazione || r.nome).filter(Boolean)))
      .catch(() => setItems([]));
  };
  useEffect(() => { loadItems(); }, []);
  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const qt = (val || "").trim();
  const filtered = (typed && qt) ? items.filter((o) => o.toLowerCase().includes(qt.toLowerCase())) : items;
  const exact = items.some((o) => o.toLowerCase() === qt.toLowerCase());
  const canAdd = false && typed && !!qt && !exact; // compagnie gestite centralmente dal superadmin

  const change = (v) => { valRef.current = v; setTyped(true); setVal(v); onChange && onChange(v); setOpen(true); };
  const pick = (o) => { valRef.current = o; setTyped(false); setVal(o); onChange && onChange(o); onCommit && onCommit(o); setOpen(false); };

  const addNew = () => {
    if (!qt) return;
    setCreateName(qt);
    setCreateOpen(true);
    setOpen(false);
  };

  const openCreate = () => { setCreateName(qt || ""); setCreateOpen(true); setOpen(false); };

  const onCreated = (record) => {
    const dn = record.denominazione || record.nome || record.ragione_sociale || createName;
    setItems((prev) => (prev.some((x) => x.toLowerCase() === dn.toLowerCase()) ? prev : [...prev, dn]));
    pick(dn);
  };

  return (
    <div className="relative w-full" ref={ref}>
      <div className="relative">
        <Input value={val} onChange={(e) => change(e.target.value)} onFocus={() => { setTyped(false); setOpen(true); }}
          onBlur={() => onCommit && onCommit(val)} data-testid={testId} autoComplete="off"
          placeholder={placeholder} className={`h-9 bg-slate-900/60 border-slate-700 pr-8 w-full ${className}`} />
        <button type="button" tabIndex={-1} onMouseDown={(e) => { e.preventDefault(); setTyped(false); setOpen((o) => !o); }} data-testid={testId ? `${testId}-toggle` : undefined}
          className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-200"><ChevronsUpDown className="h-4 w-4" /></button>
      </div>
      {open && (
        <div className="absolute z-50 mt-1 w-full rounded-md border border-slate-700 bg-[#121824] shadow-xl max-h-56 overflow-y-auto" data-testid={testId ? `${testId}-list` : undefined}>
          {filtered.map((o, i) => (
            <button key={i} type="button" onMouseDown={(e) => { e.preventDefault(); pick(o); }} data-testid={testId ? `${testId}-opt` : undefined}
              className="w-full text-left px-3 py-2 text-sm text-slate-200 hover:bg-slate-800/60 border-b border-slate-800 last:border-0">{o}</button>
          ))}
          {filtered.length === 0 && !canAdd && (
            <div className="px-3 py-2 text-xs text-slate-500">Nessuna compagnia{qt ? " corrispondente" : ""}</div>
          )}
          {canAdd && (
            <button type="button" onMouseDown={(e) => { e.preventDefault(); addNew(); }} data-testid={testId ? `${testId}-add-new` : undefined}
              className="w-full text-left px-3 py-2 text-sm bg-blue-600/20 text-blue-300 hover:bg-blue-600/30 border-t border-slate-700 flex items-center gap-2 font-medium">
              <Plus className="h-4 w-4 shrink-0" /> Aggiungi "{qt}" come nuova compagnia
            </button>
          )}
        </div>
      )}
      {createOpen && (
        <NuovaCompagniaDialog open={createOpen} onOpenChange={setCreateOpen} initialName={createName} onSaved={onCreated} />
      )}
    </div>
  );
}
